/**
 * Service class for accessing localStorage.
 */
export default function storageService() {
  /**
   * Keys used to reference data in localStorage
   */
  const SLIDER_ANSWERS = 'sliderAnswers';
  const TESTS_KEY = 'ngStorage-tests';
  const EXTRA_CHARGES_KEY = 'ngStorage-extraCharges';
  const COUPON_KEY = 'ngStorage-coupon';
  const TREATMENT_TYPE_KEY = 'ngStorage-treatmentType';
  const ORDER_UPSELLS = 'ngStorage-orderUpsells';
  const CENTER_KEY = 'ngStorage-center';
  const IS_SHIPPED_KEY = 'ngStorage-isShipped';
  const IS_FREE_KEY = 'ngStorage-free';
  const USER_ID_KEY = 'user_id';

  /**
   * Retrieves a value stored as JSON in localStorage
   *
   * @param {string} key the key to retrieve from storage
   *
   * @return {any}  The value stored in localStorage, or null if localstorage is not available
   */
  function getLocalStorage(key) {
    try {
      return JSON.parse(window.localStorage?.getItem(key) || null);
    } catch (error) {
      console.error('Error getting value from local storage', error);

      return null;
    }
  }

  /**
   * Saves a value in localStorage as a JSON string
   *
   * @param {string}  key       The key to store the value in
   * @param {any}     value     The value to be stored
   * @param {boolean} stringify Whether to stringify the value before storing it
   *
   * @return {void}
   */
  function setLocalStorage(key, value, stringify = true) {
    try {
      window.localStorage?.setItem(key, stringify ? JSON.stringify(value || null) : value);
    } catch (error) {
      console.error('Error setting value in local storage', error);
    }
  }

  /**
   * Deletes a value from the storage.
   *
   * @param key {string} The value's key.
   *
   * @return void
   */
  function removeLocalStorage(key) {
    window.localStorage?.removeItem(key);
  }

  // Initialize tests in localStorage
  if (!getLocalStorage(TESTS_KEY)) {
    setLocalStorage(TESTS_KEY, []);
  }

  return {
    /**
     * Local Storage get, set and remove
     */
    localStorage: {
      get: getLocalStorage,
      set: setLocalStorage,
      remove: removeLocalStorage,
    },

    /**
     * Retrieve selected tests data from local storage
     */
    get tests() {
      return this.localStorage.get(TESTS_KEY) ?? [];
    },

    /**
     * Retrieve is shipped data from local storage
     */
    get isShipped() {
      return this.localStorage.get(IS_SHIPPED_KEY) ?? false;
    },

    /**
     * Store selected tests data in local storage
     *
     * @param {object[]} tests the tests data to store
     */
    set tests(tests) {
      this.localStorage.set(TESTS_KEY, tests);
    },

    /**
     * Retrieve slider answers from local storage
     */
    get sliderAnswers() {
      return this.localStorage.get(SLIDER_ANSWERS) ?? {};
    },

    /**
     * Store slider answers in local storage
     *
     * @param  {object} tests the slider answers to store
     */
    set sliderAnswers(tests) {
      this.localStorage.set(SLIDER_ANSWERS, tests);
    },

    /**
     * Retrieve extra charges from local storage
     */
    get extraCharges() {
      return this.localStorage.get(EXTRA_CHARGES_KEY) ?? [];
    },

    /**
     * Store selected tests data in local storage
     *
     * @param {object[]} extraCharges the tests data to store
     */
    set extraCharges(extraCharges) {
      this.localStorage.set(EXTRA_CHARGES_KEY, extraCharges);
    },

    /**
     * Store coupon data in local storage
     *
     * @param {object} coupon the coupon data to store
     */
    set coupon(coupon) {
      this.localStorage.set(COUPON_KEY, coupon);
    },

    /**
     * Store treatment type in local storage
     *
     * @param {string} treatmentType the treatment type to store
     */
    set treatmentType(treatmentType) {
      this.localStorage.set(TREATMENT_TYPE_KEY, treatmentType);
    },

    /**
     * Store treatment type in local storage
     */
    get treatmentType() {
      return this.localStorage.get(TREATMENT_TYPE_KEY);
    },

    /**
     * Retrieve the order upsells from local storage
     */
    get orderUpsells() {
      return this.localStorage.get(ORDER_UPSELLS) ?? [];
    },

    /**
     * Store order upsells in local storage
     *
     * @param {object[]} orderUpsells the order upsells to store
     */
    set orderUpsells(orderUpsells) {
      this.localStorage.set(ORDER_UPSELLS, orderUpsells);
    },

    /**
     * Retrieve center from local storage
     */
    get center() {
      return this.localStorage.get(CENTER_KEY) ?? {};
    },

    /**
     * Store selected center data in local storage
     *
     * @param {object[]} center the center data to store
     */
    set center(center) {
      this.localStorage.set(CENTER_KEY, center);
    },

    /**
     * Retrieve the Manage user ID placing an order from local storage
     */
    get userId() {
      return getLocalStorage(USER_ID_KEY);
    },

    /**
     * Store the Manage user ID placing an order in local storage
     */
    set userId(userId) {
      if (typeof userId === 'string' && /^[0-9]+$/.test(userId.trim())) {
        setLocalStorage(USER_ID_KEY, Number(userId));
      } else if (typeof userId === 'number' && Number.isInteger(userId)) {
        setLocalStorage(USER_ID_KEY, userId);
      } else if (userId === null) {
        removeLocalStorage(USER_ID_KEY);
      } else {
        console.error(`Attempt to store invalid user_id in local storage: ${userId} (type: ${typeof userId})`);
      }
    },

    /**
     * Store whether the order is shipped in local storage
     */
    set isShipped(isShipped) {
      this.localStorage.set(IS_SHIPPED_KEY, isShipped, false);
    },

    /**
     * Store whether the order is free in local storage
     */
    set isFree(isFree) {
      this.localStorage.set(IS_FREE_KEY, isFree, false);
    },

    /**
     * Clears all checkout data from local storage
     */
    clearCheckoutData() {
      this.localStorage.remove(TREATMENT_TYPE_KEY);
      this.localStorage.remove(TESTS_KEY);
      this.localStorage.remove(ORDER_UPSELLS);
      this.localStorage.remove(EXTRA_CHARGES_KEY);
      this.localStorage.remove(COUPON_KEY);
      this.localStorage.remove(IS_SHIPPED_KEY);
      this.localStorage.remove(IS_FREE_KEY);
    },
  };
}
